/**
 * Dashboard Page
 * Overview dashboard with stats and recent activity
 */

import {
  ClipboardCheck,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  TrendingUp,
  TrendingDown,
} from 'lucide-react';

const stats = [
  {
    name: 'Pending Reviews',
    value: '24',
    change: '+12%',
    trend: 'up',
    icon: Clock,
    color: 'yellow',
  },
  {
    name: 'Approved Today',
    value: '18',
    change: '+8%',
    trend: 'up',
    icon: CheckCircle,
    color: 'green',
  },
  {
    name: 'Rejected Today',
    value: '3',
    change: '-2%',
    trend: 'down',
    icon: XCircle,
    color: 'red',
  },
  {
    name: 'Avg Review Time',
    value: '4.2h',
    change: '-15%',
    trend: 'down',
    icon: ClipboardCheck,
    color: 'blue',
  },
];

const recentVerifications = [
  {
    id: 'VER-2026-0001',
    businessName: 'Acme Services LLC',
    type: 'Business Registration',
    submittedAt: '2026-02-01T10:30:00Z',
    status: 'pending',
    priority: 'high',
  },
  {
    id: 'VER-2026-0002',
    businessName: 'TechStart Inc.',
    type: 'Identity Verification',
    submittedAt: '2026-02-01T09:15:00Z',
    status: 'pending',
    priority: 'medium',
  },
  {
    id: 'VER-2026-0003',
    businessName: 'Global Logistics Co.',
    type: 'Business Registration',
    submittedAt: '2026-02-01T08:45:00Z',
    status: 'in_review',
    priority: 'high',
  },
  {
    id: 'VER-2026-0004',
    businessName: 'Digital Solutions Ltd.',
    type: 'Document Update',
    submittedAt: '2026-02-01T07:20:00Z',
    status: 'pending',
    priority: 'low',
  },
  {
    id: 'VER-2026-0005',
    businessName: 'Smart Retail Group',
    type: 'Business Registration',
    submittedAt: '2026-02-01T06:00:00Z',
    status: 'approved',
    priority: 'medium',
  },
];

const colorClasses: Record<string, { bg: string; text: string; icon: string }> = {
  yellow: { bg: 'bg-yellow-50', text: 'text-yellow-700', icon: 'text-yellow-600' },
  green: { bg: 'bg-green-50', text: 'text-green-700', icon: 'text-green-600' },
  red: { bg: 'bg-red-50', text: 'text-red-700', icon: 'text-red-600' },
  blue: { bg: 'bg-blue-50', text: 'text-blue-700', icon: 'text-blue-600' },
};

const statusClasses: Record<string, string> = {
  pending: 'bg-yellow-100 text-yellow-800',
  in_review: 'bg-blue-100 text-blue-800',
  approved: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
};

const priorityClasses: Record<string, string> = {
  high: 'text-red-600',
  medium: 'text-yellow-600',
  low: 'text-gray-500',
};

export function DashboardPage() {
  return (
    <div className="space-y-6 animate-in">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-gray-500 mt-1">Overview of verification activities and metrics</p>
        </div>
        <button className="px-4 py-2 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors">
          + New Verification
        </button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat) => {
          const Icon = stat.icon;
          const colors = colorClasses[stat.color];
          const TrendIcon = stat.trend === 'up' ? TrendingUp : TrendingDown;
          const trendColor = stat.trend === 'up' ? 'text-green-600' : 'text-red-600';

          return (
            <div
              key={stat.name}
              className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">{stat.name}</p>
                  <p className="text-3xl font-bold text-gray-900 mt-2">{stat.value}</p>
                </div>
                <div className={`p-3 rounded-lg ${colors.bg}`}>
                  <Icon className={`w-6 h-6 ${colors.icon}`} />
                </div>
              </div>
              <div className="flex items-center gap-2 mt-4">
                <TrendIcon className={`w-4 h-4 ${trendColor}`} />
                <span className={`text-sm font-medium ${trendColor}`}>{stat.change}</span>
                <span className="text-sm text-gray-400">vs yesterday</span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Verifications */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200 shadow-sm">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Recent Verifications</h2>
              <button className="text-sm text-primary-600 hover:text-primary-700 font-medium">
                View All
              </button>
            </div>
          </div>
          <div className="divide-y divide-gray-200">
            {recentVerifications.map((verification) => (
              <div
                key={verification.id}
                className="p-4 hover:bg-gray-50 transition-colors cursor-pointer"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center">
                      <ClipboardCheck className="w-5 h-5 text-primary-600" />
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900">{verification.businessName}</h3>
                      <p className="text-sm text-gray-500">
                        {verification.id} • {verification.type}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <span
                      className={`text-xs font-medium px-2.5 py-1 rounded-full ${priorityClasses[verification.priority]}`}
                    >
                      {verification.priority.charAt(0).toUpperCase() +
                        verification.priority.slice(1)} Priority
                    </span>
                    <span
                      className={`text-xs font-medium px-2.5 py-1 rounded-full ${statusClasses[verification.status]}`}
                    >
                      {verification.status.replace('_', ' ').toUpperCase()}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Quick Actions & Alerts */}
        <div className="space-y-6">
          {/* Quick Actions */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
            <div className="space-y-3">
              <button className="w-full flex items-center gap-3 p-3 rounded-lg border border-gray-200 hover:border-primary-300 hover:bg-primary-50 transition-colors text-left">
                <ClipboardCheck className="w-5 h-5 text-primary-600" />
                <span className="font-medium text-gray-700">Start New Review</span>
              </button>
              <button className="w-full flex items-center gap-3 p-3 rounded-lg border border-gray-200 hover:border-primary-300 hover:bg-primary-50 transition-colors text-left">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <span className="font-medium text-gray-700">Bulk Approve</span>
              </button>
              <button className="w-full flex items-center gap-3 p-3 rounded-lg border border-gray-200 hover:border-primary-300 hover:bg-primary-50 transition-colors text-left">
                <AlertCircle className="w-5 h-5 text-yellow-600" />
                <span className="font-medium text-gray-700">Flag for Review</span>
              </button>
            </div>
          </div>

          {/* System Status */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">System Status</h2>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Gateway API</span>
                <span className="flex items-center gap-2 text-green-600">
                  <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                  Operational
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600">D1 Database</span>
                <span className="flex items-center gap-2 text-green-600">
                  <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                  Operational
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Verification Queue</span>
                <span className="flex items-center gap-2 text-green-600">
                  <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                  Processing
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
